<template>
  <div class="analytics-page">
    <div class="analytics-header">
      <h2 class="analytics-title">Analytics</h2>
      <div class="analytics-controls">
        <select v-model="preset" class="preset-select" @change="onPresetChange">
          <option value="7d">Senaste 7 dagarna</option>
          <option value="30d">Senaste 30 dagarna</option>
          <option value="thisMonth">Denna månad</option>
          <option value="lastMonth">Förra månaden</option>
        </select>
        <label class="compare-label">
          <span class="toggle-switch">
            <input type="checkbox" v-model="showComparison" />
            <span class="toggle-knob"></span>
          </span>
          <span class="compare-text">Jämför med föregående period</span>
        </label>
      </div>
    </div>

    <!-- Stat-kort -->
    <div class="stats-row">
      <div class="stat-card">
        <div class="stat-value">{{ fmt(totalPageviews) }}</div>
        <div class="stat-label">Sidvisningar</div>
        <div
          v-if="showComparison && compData.length"
          class="stat-diff"
          :class="diffClass(totalPageviews, compTotalPageviews)"
        >
          {{ diffLabel(totalPageviews, compTotalPageviews) }} vs föregående
        </div>
      </div>

      <!-- Exakta månadsbaserade unika besökare -->
      <div class="stat-card stat-card--highlight">
        <div class="stat-value">{{ monthlyUniqueTotal !== null ? fmt(monthlyUniqueTotal) : '—' }}</div>
        <div class="stat-label">Unika besökare (exakt)</div>
        <div class="stat-hint">Deduplicerade per månad</div>
      </div>

      <div class="stat-card">
        <div class="stat-value">{{ fmt(avgPageviews) }}</div>
        <div class="stat-label">Snitt sidvisningar/dag</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ fmt(avgUnique) }}</div>
        <div class="stat-label">Snitt dagliga besök/dag</div>
      </div>
    </div>

    <!-- Graf-container -->
    <div class="chart-wrapper">
      <div v-if="loading" class="chart-state">Laddar data…</div>
      <div v-else-if="error" class="chart-state chart-state--error">{{ error }}</div>
      <div v-else-if="filledData.length === 0" class="chart-state">Ingen data för vald period.</div>

      <template v-else>
        <!-- Legenden ovanför grafen -->
        <div class="chart-legend">
          <span class="legend-item">
            <span class="legend-line" style="background:#cc1100"></span>
            Sidvisningar
          </span>
          <span class="legend-item">
            <span class="legend-line" style="background:#2563eb"></span>
            Unika besökare/dag
          </span>
          <template v-if="showComparison && compFilledData.length">
            <span class="legend-item legend-item--comp">
              <span class="legend-line legend-line--dashed" style="border-color:#cc1100"></span>
              Föregående period (sidvisningar)
            </span>
            <span class="legend-item legend-item--comp">
              <span class="legend-line legend-line--dashed" style="border-color:#2563eb"></span>
              Föregående period (unika)
            </span>
          </template>
        </div>

        <!-- SVG-graf -->
        <svg
          :viewBox="`0 0 ${W} ${H}`"
          class="chart-svg"
          xmlns="http://www.w3.org/2000/svg"
          aria-label="Trafikgraf"
        >
          <!-- Horisontella gridlinjer -->
          <line
            v-for="gl in yGrid"
            :key="gl.y"
            :x1="PL"
            :y1="gl.y"
            :x2="W - PR"
            :y2="gl.y"
            stroke="var(--color-border, #e5e7eb)"
            stroke-width="1"
          />

          <!-- Y-axel-etiketter -->
          <text
            v-for="gl in yGrid"
            :key="`yl-${gl.y}`"
            :x="PL - 8"
            :y="gl.y + 4"
            text-anchor="end"
            font-size="11"
            fill="var(--color-text-secondary, #9ca3af)"
          >{{ gl.label }}</text>

          <!-- X-axel-etiketter -->
          <text
            v-for="xl in xLabels"
            :key="`xl-${xl.i}`"
            :x="xScale(xl.i)"
            :y="H - PB + 16"
            text-anchor="middle"
            font-size="11"
            fill="var(--color-text-secondary, #9ca3af)"
          >{{ xl.label }}</text>

          <!-- Jämförelselinjer (streckade, bakgrundsskikt) -->
          <template v-if="showComparison && compFilledData.length">
            <polyline
              :points="compPageviewPoints"
              fill="none"
              stroke="#cc1100"
              stroke-width="1.5"
              stroke-dasharray="5 4"
              opacity="0.45"
              stroke-linejoin="round"
              stroke-linecap="round"
            />
            <polyline
              :points="compUniquePoints"
              fill="none"
              stroke="#2563eb"
              stroke-width="1.5"
              stroke-dasharray="5 4"
              opacity="0.45"
              stroke-linejoin="round"
              stroke-linecap="round"
            />
          </template>

          <!-- Skuggat area under sidvisningslinjen -->
          <path
            :d="pageviewArea"
            fill="#cc1100"
            opacity="0.07"
          />
          <path
            :d="uniqueArea"
            fill="#2563eb"
            opacity="0.07"
          />

          <!-- Huvudlinjer -->
          <polyline
            :points="pageviewPoints"
            fill="none"
            stroke="#cc1100"
            stroke-width="2.5"
            stroke-linejoin="round"
            stroke-linecap="round"
          />
          <polyline
            :points="uniquePoints"
            fill="none"
            stroke="#2563eb"
            stroke-width="2.5"
            stroke-linejoin="round"
            stroke-linecap="round"
          />

          <!-- Datapunkter (cirklar) -->
          <circle
            v-for="(d, i) in filledData"
            :key="`pv-${i}`"
            :cx="xScale(i)"
            :cy="yScale(d.pageviews)"
            r="3"
            fill="#cc1100"
          />
          <circle
            v-for="(d, i) in filledData"
            :key="`uv-${i}`"
            :cx="xScale(i)"
            :cy="yScale(d.uniqueVisitors)"
            r="3"
            fill="#2563eb"
          />
        </svg>

        <!-- Datumintervall under grafen -->
        <div class="chart-range-label">
          {{ rangeLabel }}
          <span v-if="showComparison && compRangeLabel" class="comp-range-label">
            · Jämför med: {{ compRangeLabel }}
          </span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import axios from "axios";

// ── Datumhjälpare ──────────────────────────────────────────────────────────

function toYMD(date) {

  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function allDatesInRange(fromStr, toStr) {
  const dates = [];
  let cur = new Date(fromStr + "T00:00:00");
  const end = new Date(toStr + "T00:00:00");
  while (cur <= end) {
    dates.push(toYMD(cur));
    cur = addDays(cur, 1);
  }
  return dates;
}

function getPresetRange(preset) {
  const now = new Date();
  if (preset === "7d") {
    return { from: toYMD(addDays(now, -6)), to: toYMD(now) };
  }
  if (preset === "30d") {
    return { from: toYMD(addDays(now, -29)), to: toYMD(now) };
  }
  if (preset === "thisMonth") {
    return {
      from: toYMD(new Date(now.getFullYear(), now.getMonth(), 1)),
      to: toYMD(now),
    };
  }
  if (preset === "lastMonth") {
    const firstOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastOfLastMonth = addDays(firstOfThisMonth, -1);
    const firstOfLastMonth = new Date(
      lastOfLastMonth.getFullYear(),
      lastOfLastMonth.getMonth(),
      1
    );
    return {
      from: toYMD(firstOfLastMonth),
      to: toYMD(lastOfLastMonth),
    };
  }
  return { from: toYMD(addDays(now, -29)), to: toYMD(now) };
}

function getCompRange(from, to) {
  const fromD = new Date(from + "T00:00:00");
  const toD = new Date(to + "T00:00:00");
  const days =
    Math.round((toD - fromD) / (1000 * 60 * 60 * 24)) + 1;
  const compTo = addDays(fromD, -1);
  const compFrom = addDays(compTo, -(days - 1));
  return { from: toYMD(compFrom), to: toYMD(compTo) };
}

function formatDate(ymd) {
  const d = new Date(ymd + "T00:00:00");
  return d.toLocaleDateString("sv-SE", { day: "numeric", month: "short" });
}

// ── SVG-konstanter ─────────────────────────────────────────────────────────
const W = 800;
const H = 300;
const PL = 48; // padding left (Y-etiketter)
const PR = 16; // padding right
const PT = 16; // padding top
const PB = 36; // padding bottom (X-etiketter)

const PLOT_W = W - PL - PR;
const PLOT_H = H - PT - PB;

export default {
  name: "AdminAnalytics",

  data() {
    return {
      preset: "30d",
      showComparison: false,
      loading: false,
      error: null,
      data: [],
      compData: [],
      monthlyData: [],
      from: "",
      to: "",
      compFrom: "",
      compTo: "",
      // SVG-konstanter tillgängliga i template
      W,
      H,
      PL,
      PR,
      PT,
      PB,
    };
  },

  computed: {
    // ── Fyllda dataserier (en post per dag, saknade dagar = 0) ────────────

    filledData() {
      if (!this.from || !this.to) return [];
      const map = {};
      this.data.forEach((d) => { map[d.date] = d; });
      return allDatesInRange(this.from, this.to).map((date) => ({
        date,
        pageviews: map[date]?.pageviews ?? 0,
        uniqueVisitors: map[date]?.uniqueVisitors ?? 0,
      }));
    },

    compFilledData() {
      if (!this.compFrom || !this.compTo || !this.compData.length) return [];
      const map = {};
      this.compData.forEach((d) => { map[d.date] = d; });
      return allDatesInRange(this.compFrom, this.compTo).map((date) => ({
        date,
        pageviews: map[date]?.pageviews ?? 0,
        uniqueVisitors: map[date]?.uniqueVisitors ?? 0,
      }));
    },

    // ── Summerings-stats ──────────────────────────────────────────────────

    totalPageviews() {
      return this.filledData.reduce((s, d) => s + d.pageviews, 0);
    },
    totalUnique() {
      return this.filledData.reduce((s, d) => s + d.uniqueVisitors, 0);
    },
    avgPageviews() {
      if (!this.filledData.length) return 0;
      return Math.round(this.totalPageviews / this.filledData.length);
    },
    avgUnique() {
      if (!this.filledData.length) return 0;
      return Math.round(this.totalUnique / this.filledData.length);
    },
    compTotalPageviews() {
      return this.compFilledData.reduce((s, d) => s + d.pageviews, 0);
    },
    compTotalUnique() {
      return this.compFilledData.reduce((s, d) => s + d.uniqueVisitors, 0);
    },

    // ── Exakta månadsbaserade unika besökare ──────────────────────────────
    // Summerar uniqueVisitors från /monthly-summary (deduplicerat per månad).
    // Notera: en person som besöker i maj och juni räknas 2 ggr i totalen,
    // men aldrig mer än 1 gång inom samma månad.
    monthlyUniqueTotal() {
      if (!this.monthlyData.length) return null;
      return this.monthlyData.reduce((s, m) => s + m.uniqueVisitors, 0);
    },

    // ── Y-skala ───────────────────────────────────────────────────────────

    yMax() {
      const vals = this.filledData.flatMap((d) => [d.pageviews, d.uniqueVisitors]);
      if (this.showComparison) {
        this.compFilledData.forEach((d) => {
          vals.push(d.pageviews, d.uniqueVisitors);
        });
      }
      return Math.max(...vals, 5);
    },

    yGrid() {
      const steps = 4;
      const lines = [];
      for (let i = 0; i <= steps; i++) {
        const val = Math.round((this.yMax / steps) * i);
        const y = PT + PLOT_H - (val / this.yMax) * PLOT_H;
        lines.push({ y, label: val >= 1000 ? (val / 1000).toFixed(1) + "k" : String(val) });
      }
      return lines;
    },

    // ── X-etiketter (max ~8 synliga) ──────────────────────────────────────

    xLabels() {
      const n = this.filledData.length;
      if (n === 0) return [];
      const step = Math.max(1, Math.floor(n / 8));
      const labels = [];
      for (let i = 0; i < n; i += step) {
        labels.push({ i, label: formatDate(this.filledData[i].date) });
      }
      // Inkludera alltid sista
      if (labels[labels.length - 1]?.i !== n - 1) {
        labels.push({ i: n - 1, label: formatDate(this.filledData[n - 1].date) });
      }
      return labels;
    },

    // ── SVG-koordinathjälpare ─────────────────────────────────────────────

    pageviewPoints() {
      return this.toPoints(this.filledData, "pageviews");
    },
    uniquePoints() {
      return this.toPoints(this.filledData, "uniqueVisitors");
    },
    compPageviewPoints() {
      return this.toPoints(this.compFilledData, "pageviews");
    },
    compUniquePoints() {
      return this.toPoints(this.compFilledData, "uniqueVisitors");
    },

    // ── Area-paths (skuggning under linjerna) ─────────────────────────────

    pageviewArea() {
      return this.toAreaPath(this.filledData, "pageviews");
    },
    uniqueArea() {
      return this.toAreaPath(this.filledData, "uniqueVisitors");
    },

    // ── Datumintervall-etiketter ──────────────────────────────────────────

    rangeLabel() {
      if (!this.from || !this.to) return "";
      return `${formatDate(this.from)} – ${formatDate(this.to)}`;
    },
    compRangeLabel() {
      if (!this.compFrom || !this.compTo) return "";
      return `${formatDate(this.compFrom)} – ${formatDate(this.compTo)}`;
    },
  },

  watch: {
    showComparison(val) {
      if (val && !this.compData.length) {
        this.fetchCompData();
      }
    },
  },

  mounted() {
    this.onPresetChange();
  },

  methods: {
    // ── Skalningsfunktioner ───────────────────────────────────────────────

    xScale(i) {
      const n = this.filledData.length;
      if (n <= 1) return PL + PLOT_W / 2;
      return PL + (i / (n - 1)) * PLOT_W;
    },

    yScale(val) {
      return PT + PLOT_H - (val / this.yMax) * PLOT_H;
    },

    toPoints(dataset, key) {
      if (!dataset.length) return "";
      return dataset
        .map((d, i) => `${this.xScale(i)},${this.yScale(d[key])}`)
        .join(" ");
    },

    toAreaPath(dataset, key) {
      if (!dataset.length) return "";
      const baseline = PT + PLOT_H;
      const n = dataset.length;
      const pts = dataset
        .map((d, i) => `${this.xScale(i)},${this.yScale(d[key])}`)
        .join(" L ");
      const first = `${this.xScale(0)},${baseline}`;
      const last = `${this.xScale(n - 1)},${baseline}`;
      return `M ${first} L ${pts} L ${last} Z`;
    },

    // ── Data-hämtning ────────────────────────────────────────────────────

    onPresetChange() {
      const range = getPresetRange(this.preset);
      this.from = range.from;
      this.to = range.to;
      const comp = getCompRange(this.from, this.to);
      this.compFrom = comp.from;
      this.compTo = comp.to;
      this.compData = [];
      this.monthlyData = [];
      this.fetchData();
      this.fetchMonthlyData();
      if (this.showComparison) {
        this.fetchCompData();
      }
    },

    async fetchData() {
      this.loading = true;
      this.error = null;
      try {
        const token = localStorage.getItem("token");
        const { data } = await axios.get(
          process.env.VUE_APP_API_URL + "analytics/summary",
          {
            params: { from: this.from, to: this.to },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        this.data = data;
      } catch (err) {
        this.error =
          err.response?.data?.message || "Kunde inte hämta analytics-data.";
      } finally {
        this.loading = false;
      }
    },

    async fetchCompData() {
      try {
        const token = localStorage.getItem("token");
        const { data } = await axios.get(
          process.env.VUE_APP_API_URL + "analytics/summary",
          {
            params: { from: this.compFrom, to: this.compTo },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        this.compData = data;
      } catch {
        // Jämförelsedata är nice-to-have — vi ignorerar fel tyst
      }
    },

    async fetchMonthlyData() {
      try {
        const token = localStorage.getItem("token");
        const { data } = await axios.get(
          process.env.VUE_APP_API_URL + "analytics/monthly-summary",
          {
            params: { from: this.from, to: this.to },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        this.monthlyData = data;
      } catch {
        // Månadsdata är nice-to-have — vi ignorerar fel tyst
      }
    },

    // ── Formatering ───────────────────────────────────────────────────────

    fmt(n) {
      return n.toLocaleString("sv-SE");
    },

    diffLabel(current, previous) {
      if (!previous) return "+100%";
      const pct = Math.round(((current - previous) / previous) * 100);
      return pct >= 0 ? `+${pct}%` : `${pct}%`;
    },

    diffClass(current, previous) {
      if (current >= previous) return "diff-up";
      return "diff-down";
    },
  },
};
</script>

<style scoped>
.analytics-page {
  max-width: 960px;
}

/* ── Header ── */
.analytics-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.analytics-title {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  color: var(--color-text);
}

.analytics-controls {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  flex-wrap: wrap;
}

.preset-select {
  appearance: none;
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  color: var(--color-text);
  font-size: 0.8rem;
  font-family: inherit;
  font-weight: 600;
  letter-spacing: 0.05em;
  padding: 0.4rem 2rem 0.4rem 0.75rem;
  border-radius: 4px;
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.5rem center;
}

.preset-select:focus {
  outline: 2px solid var(--color-accent, #cc1100);
  outline-offset: 2px;
}

/* ── Toggle-switch ── */
.compare-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
}

.compare-text {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--color-text-secondary, #888);
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-knob {
  position: absolute;
  inset: 0;
  background: var(--color-border, #d1d5db);
  border-radius: 20px;
  transition: background 0.2s;
}

.toggle-knob::before {
  content: "";
  position: absolute;
  width: 14px;
  height: 14px;
  left: 3px;
  top: 3px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.2s;
}

.toggle-switch input:checked + .toggle-knob {
  background: var(--color-accent, #cc1100);
}

.toggle-switch input:checked + .toggle-knob::before {
  transform: translateX(16px);
}

/* ── Stat-kort ── */
.stats-row {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-card {
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 1rem 1.25rem;
}

.stat-value {
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--color-text);
  line-height: 1.1;
}

.stat-label {
  font-size: 0.75rem;
  color: var(--color-text-secondary, #888);
  margin-top: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
}

.stat-hint {
  font-size: 0.7rem;
  color: var(--color-text-secondary, #aaa);
  margin-top: 0.2rem;
  font-style: italic;
}

.stat-card--highlight {
  border-color: var(--color-accent, #cc1100);
  border-width: 2px;
}

.stat-diff {
  font-size: 0.75rem;
  font-weight: 700;
  margin-top: 0.5rem;
}

.diff-up { color: #16a34a; }
.diff-down { color: #dc2626; }

/* ── Graf-wrapper ── */
.chart-wrapper {
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 1.25rem 1.25rem 1rem;
}

.chart-state {
  text-align: center;
  padding: 3rem 0;
  color: var(--color-text-secondary, #888);
  font-size: 0.875rem;
}

.chart-state--error {
  color: #dc2626;
}

/* ── Legend ── */
.chart-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 0.75rem;
  font-size: 0.78rem;
  color: var(--color-text-secondary, #888);
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-item--comp {
  opacity: 0.65;
}

.legend-line {
  display: inline-block;
  width: 22px;
  height: 3px;
  border-radius: 2px;
  flex-shrink: 0;
}

.legend-line--dashed {
  background: none !important;
  border-top: 2px dashed;
  height: 0;
  margin-top: 1px;
}

/* ── SVG ── */
.chart-svg {
  width: 100%;
  display: block;
  overflow: visible;
}

/* ── Datumintervall ── */
.chart-range-label {
  text-align: center;
  font-size: 0.75rem;
  color: var(--color-text-secondary, #9ca3af);
  margin-top: 0.5rem;
}

.comp-range-label {
  opacity: 0.7;
}

/* ── Responsiv ── */
@media (max-width: 600px) {
  .analytics-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .stats-row {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
